import { FormattedMessage } from 'react-intl';

import BrakeLiningsStatus, { BRAKE_LININGS_STATUS_MESSAGE_IDS } from '@/features/diagnostics/sidebar/vehicleData/BreakLiningsStatus';
import { Asset, AxlePosition, BrakeLinings, BrakeLiningsAxle, Criticality } from '@/generated/fleetAggregationServiceTypes';
import { FeatureToggle, isFeatureToggleEnabled } from '@/hooks/useFeatureToggle';
import useVisibilityTracking from '@/hooks/useVisibilityTracking';
import { hasFeature, isManAsset } from '@/productsConfig';
import AnalyticsEvents from '@/analytics/AnalyticsEvents';
import { BrakeLiningsInfoTooltip } from './BrakeLiningsInfoTooltip';
import { NotSyncedInfoTooltip } from './NotSyncedInfoTooltip';
import { OverviewTable } from './OverviewTable';

type SidebarBrakeLiningsProps = {
    brakeLinings: BrakeLinings;
    asset: Asset;
};

type EnrichedAxleItems = Required<BrakeLiningsAxle['items']>;

const enrichAxleItems = (axleItems: BrakeLiningsAxle['items']): EnrichedAxleItems => ({
    left: axleItems.left || { criticality: Criticality.Unknown },
    right: axleItems.right || { criticality: Criticality.Unknown },
});

const SidebarBrakeLinings = ({ brakeLinings, asset }: SidebarBrakeLiningsProps) => {
    const overview = brakeLinings.overview;
    const noOverviewData = !overview.warning && !overview.danger && !overview.normal && !overview.unknown;
    const isTGE = asset.hasOCU3DeviceType;
    const isServiceCareLAlphaEnabled = isFeatureToggleEnabled(FeatureToggle.ENABLE_SERVICE_CARE_L_ALPHA);
    const brakeLiningsRef = useVisibilityTracking<HTMLDivElement>(() => {
        AnalyticsEvents.viewSidebarBrakeLinings();
    });

    const renderContent = () => {
        if (isTGE)
            return (
                <div className="margin-bottom-15">
                    <FormattedMessage id="intl-msg:fleetstatus.diagnostics.status.not_applicable" />
                </div>
            );
        if (noOverviewData)
            return (
                <div className="margin-bottom-15">
                    <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.no_data" />
                </div>
            );
        if (hasFeature('diagnosticDetailsView', asset))
            return (
                <table className="table table-condensed padding-bottom-15">
                    <thead>
                        <tr>
                            <th className="text-center text-normal">
                                <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.axle.left" />
                            </th>
                            <th className="text-center text-normal">
                                <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.axle" />
                            </th>
                            <th className="text-center text-normal">
                                <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.axle.right" />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {brakeLinings.axles.map(({ position, items }) => {
                            const { left, right }: EnrichedAxleItems = enrichAxleItems(items);

                            return (
                                <tr key={position} className="width-100pct">
                                    <td className="width-40pct">
                                        <BrakeLiningsStatus brakeLiningMeasurement={left} />
                                    </td>
                                    <td className="border-style-solid border-bottom-0 border-width-1 border-color-light padding-15 text-center width-10pct">
                                        {position === AxlePosition.ReserveWheels ? '' : position}
                                    </td>
                                    <td className="padding-15 width-40pct">
                                        <BrakeLiningsStatus brakeLiningMeasurement={right} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            );
        return <OverviewTable overview={overview} statusMessageIds={isServiceCareLAlphaEnabled ? BRAKE_LININGS_STATUS_MESSAGE_IDS : undefined} />;
    };

    return (
        <div ref={brakeLiningsRef} data-testid="sidebar-brake-linings">
            <h6 className="text-uppercase text-color-dark">
                <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.section.brake_linings" />
                {!isTGE && (
                    <>
                        <BrakeLiningsInfoTooltip />
                        {isManAsset(asset) && <NotSyncedInfoTooltip measured_at_date={brakeLinings.measured_at_date} measured_at_distance={brakeLinings.measured_at_distance} />}
                    </>
                )}
            </h6>
            {isManAsset(asset) ? (
                renderContent()
            ) : (
                <div className="margin-bottom-15">
                    <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.non_man_vehicles.data_not_received" />
                </div>
            )}
        </div>
    );
};

export default SidebarBrakeLinings;
